import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { storefrontsApi } from '../api/storefronts';
import { useAuth } from '../hooks/useAuth';

interface PendingStorefrontsValue {
  /** Shops awaiting a review decision. 0 while unknown. */
  count: number;
  /** Re-fetch immediately — call after approving or rejecting one. */
  refresh: () => Promise<void>;
}

const PendingStorefrontsContext = createContext<PendingStorefrontsValue>({
  count: 0,
  refresh: async () => {},
});

const POLL_INTERVAL_MS = 60_000;

/** Same shape as PendingApprovalsContext, deliberately not merged with it —
 *  the two queues are reviewed by different people under different
 *  permissions, and a shared context would mean either fetching both on
 *  every poll regardless of who's logged in, or the badge logic reaching
 *  back into a context that knows about a queue it has no permission for. */
export const PendingStorefrontsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isAuthenticated, can } = useAuth();
  const [count, setCount] = useState(0);

  const canFetch = isAuthenticated && can('storefront.review');

  const refresh = useCallback(async () => {
    if (!canFetch) {
      setCount(0);
      return;
    }
    try {
      // queue() caps at 100 server-side with no total returned separately —
      // fine for a badge at this queue's actual volume; would need the
      // endpoint to return a real total before this number means anything
      // past 100 waiting at once.
      const rows = await storefrontsApi.queue('in_review');
      setCount(rows.length);
    } catch {
      // Badge is best-effort; leave the last known count on transient errors.
    }
  }, [canFetch]);

  useEffect(() => {
    if (!canFetch) {
      setCount(0);
      return;
    }
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [canFetch, refresh]);

  return (
    <PendingStorefrontsContext.Provider value={{ count, refresh }}>
      {children}
    </PendingStorefrontsContext.Provider>
  );
};

export const usePendingStorefronts = () => useContext(PendingStorefrontsContext);
